// A delivery queue, never a substitute for Firestore. Only a server acknowledgement
// removes an item. Namespace by Firebase UID so a shared iPad cannot replay another
// student's transcript into the active account.
const database = () => new Promise((resolve, reject) => {
  const request = indexedDB.open('charlie-charlie-delivery', 1);
  request.onupgradeneeded = () => request.result.createObjectStore('pending', { keyPath: 'id' });
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});
async function transaction(action, mode = 'readonly') {
  const db = await database();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pending', mode);
    const request = action(tx.objectStore('pending'));
    let result;
    request.onsuccess = () => { result = request.result; };
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error); };
  });
}
const TERMINAL = new Set(['permission-denied', 'failed-precondition', 'invalid-argument', 'not-found', 'already-exists']);
const errorCode = error => String(error?.code ?? '').replace(/^functions\//, '');
const terminal = error => TERMINAL.has(errorCode(error));
async function markFailed(id, error) {
  const failure = { code: errorCode(error), message: String(error?.message ?? '') };
  const db = await database();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pending', 'readwrite');
    const store = tx.objectStore('pending');
    const request = store.get(id);
    request.onsuccess = () => { if (request.result) store.put({ ...request.result, failed: true, failedAt: Date.now(), failure }); };
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error); };
  });
}
export async function enqueue(uid, sessionId, operation, payload) {
  const id = `${uid}:${sessionId}:${operation}:${payload.eventId}`;
  await transaction(store => store.put({ id, uid, sessionId, operation, payload, createdAt: Date.now() }), 'readwrite');
  return id;
}
export const acknowledge = id => transaction(store => store.delete(id), 'readwrite');
export async function pendingFor(uid, sessionId) {
  const items = await transaction(store => store.getAll());
  return items.filter(item => !item.failed && item.uid === uid && (!sessionId || item.sessionId === sessionId)).sort((a, b) => a.createdAt - b.createdAt);
}
export async function failedFor(uid, sessionId) {
  const items = await transaction(store => store.getAll());
  return items.filter(item => item.failed && item.uid === uid && (!sessionId || item.sessionId === sessionId)).sort((a, b) => a.createdAt - b.createdAt);
}
export async function deliver(uid, sessionId, operation, payload, sender) {
  const id = await enqueue(uid, sessionId, operation, payload);
  let result;
  try { result = await sender(sessionId, payload); }
  catch (error) { if (terminal(error)) await markFailed(id, error).catch(() => {}); throw error; }
  if (result?.saved !== true) throw new Error('No Firestore acknowledgement was received. The transcript remains pending.');
  await acknowledge(id);
  return result;
}
export async function retryDelivery(uid, handlers, sessionId) {
  const results = [];
  for (const item of await pendingFor(uid, sessionId)) {
    let result;
    try { result = await handlers[item.operation](item.sessionId, item.payload); }
    catch (error) { if (!terminal(error)) throw error; await markFailed(item.id, error).catch(() => {}); continue; }
    if (result?.saved !== true) throw new Error('No Firestore acknowledgement was received. The transcript remains pending.');
    await acknowledge(item.id);
    results.push(result);
  }
  return results;
}
